import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, MapPinned, BookMarked, LogOut, Compass, UserCircle2, MapPin, BarChart3, FileText, ClipboardList } from "lucide-react";
import { resolveImageUrl } from "@/components/ImageUploader";

const NAV = [
    { to: "/dashboard", label: "Übersicht", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/trips", label: "Reisen", icon: MapPinned, testid: "nav-trips" },
    { to: "/content", label: "Content-DB", icon: BookMarked, testid: "nav-content" },
    { to: "/profile", label: "Profil", icon: UserCircle2, testid: "nav-profile" },
    { to: "/local", label: "Local", icon: MapPin, testid: "nav-local" },
    { to: "/stats", label: "Statistik", icon: BarChart3, testid: "nav-stats" },
    { to: "/invoices", label: "Rechnungen", icon: FileText, testid: "nav-invoices" },
    { to: "/ops", label: "Ops Planner", icon: ClipboardList, testid: "nav-ops" },
];

export default function AppLayout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-sand flex">
            {/* Sidebar */}
            <aside className="w-64 bg-sand-deep border-r border-sand-300 flex flex-col fixed inset-y-0 left-0 z-30">
                <div className="px-6 py-7 border-b border-sand-300">
                    <div className="flex items-center gap-2.5">
                        {user?.brand_logo_url ? (
                            <div
                                className="w-9 h-9 rounded-md overflow-hidden bg-sand-deep border border-sand-300 flex-shrink-0"
                                data-testid="sidebar-brand-logo"
                            >
                                <img
                                    src={resolveImageUrl(user.brand_logo_url)}
                                    alt="Logo"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        ) : (
                            <div className="w-9 h-9 rounded-md bg-navy flex items-center justify-center flex-shrink-0">
                                <Compass className="w-5 h-5 text-sand" strokeWidth={1.75} />
                            </div>
                        )}
                        <div className="leading-none min-w-0">
                            <div
                                className="text-[15px] font-bold tracking-tight text-ink truncate"
                                style={{ fontFamily: "Space Grotesk" }}
                                data-testid="sidebar-brand-name"
                                title={user?.brand_name || "Pro-Travel"}
                            >
                                {user?.brand_name?.trim() || "Pro-Travel"}
                            </div>
                            <div
                                className="text-[10px] uppercase tracking-[0.18em] text-ink-tertiary mt-1 truncate"
                                data-testid="sidebar-brand-subtitle"
                            >
                                {user?.brand_subtitle?.trim() || "Planner"}
                            </div>
                        </div>
                    </div>
                </div>

                <nav className="flex-1 px-3 py-5 space-y-1">
                    {NAV.map(({ to, label, icon: Icon, testid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            data-testid={testid}
                            className={({ isActive }) =>
                                `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-white text-ink shadow-card border border-sand-300"
                                        : "text-ink-secondary hover:bg-white/60 hover:text-ink"
                                }`
                            }
                            end={to === "/dashboard"}
                        >
                            <Icon className="w-4 h-4" strokeWidth={1.75} />
                            <span>{label}</span>
                        </NavLink>
                    ))}
                </nav>

                <div className="px-3 pb-3 pt-2 border-t border-sand-300 mt-auto">
                    <div className="px-3 py-3 mb-2">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-ink-tertiary mb-1">Angemeldet als</p>
                        <p className="text-sm font-semibold text-ink truncate" data-testid="sidebar-user-name">{user?.name || "—"}</p>
                        <p className="text-xs text-ink-tertiary truncate">{user?.email}</p>
                    </div>
                    <button
                        type="button"
                        onClick={handleLogout}
                        data-testid="logout-button"
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-ink-secondary hover:bg-white/60 hover:text-terracotta transition-colors"
                    >
                        <LogOut className="w-4 h-4" strokeWidth={1.75} />
                        Abmelden
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 ml-64">
                <div className="max-w-[1400px] mx-auto px-8 lg:px-12 py-10">{children}</div>
            </main>
        </div>
    );
}
